import { ShippingServiceModelType } from '@teinor/erp/types/company/config/shipping/shippingCompany/shippingService'
import { WorkingCenterModelType } from '@teinor/erp/types/company/workingCenter'
import { WorkingCenterShippingServiceModelType } from '@teinor/erp/types/company/workingCenter/workingCenterShippingService'
import { useCallback, useMemo } from 'react'
import { ComponentWithPermissions } from '../../../../../../../../../baseComponents/CreateRoutering/types'
import ModalSmallFilled from '../../../../../../../../../baseComponents/ModalsLayouts/ModalSmallFilled'
import { ModalEditComponentProps } from '../../../../../../../../../baseComponents/ModalsLayouts/types'
import { permissionCheck } from '../../../../../../../../../baseComponents/PermissionChecker/function'
import TwinTable from '../../../../../../../../../baseComponents/TwinTable'
import BaseScreenTableInner from '../../../../../../../../../baseComponents/TwinTable/BaseScreenTableInner'
import { TableComponentBaseScreenInnerProps } from '../../../../../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderBoolean, CustomRenderString, DeleteRow } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { twinFetchPostText } from '../../../../../../../../../utils/globals/data'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import ModalCreateShippingServiceWorkingCenter from './ModalCreateShippingServiceWorkingCenter'
import ModalDeleteShippingServiceWorkingCenter from './ModalDeleteShippingServiceWorkingCenter'
import ShippingServiceWorkingCenterSortButton from './ShippingServiceWorkingCenterSortButton'

type WorkingCenterShippingServiceModelTypeExtended = WorkingCenterShippingServiceModelType & ShippingServiceModelType & {
    'ShippingService.name': string,
    'ShippingService.ShippingCompany.name': string
}

interface ShippingServicesWorkingCenterProps extends ComponentWithPermissions {
    basicData: WorkingCenterModelType | null
}

const ShippingServicesWorkingCenter: React.FC<ShippingServicesWorkingCenterProps> = ({ basicData, userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('shippingServices', 'Servicios de transporte'),
        newText: t('addShippingService', 'Añadir servicio de transporte'),
    }

    return (
        <BaseScreenTableInner TableComponent={TableShippingServicesWorkingCenter} extraWhereParams={{ 'WorkingCenterId': basicData?.id }} pageData={pageData} CreateModalComponent={ModalCreateShippingServiceWorkingCenter} DeleteModalComponent={ModalDeleteShippingServiceWorkingCenter} EditModalComponent={ModalCustomRenderBoolean} userPermissions={userPermissions} />
    )
}
interface TableShippingServicesWorkingCenterProps extends TableComponentBaseScreenInnerProps {

 }

const TableShippingServicesWorkingCenter: React.FC<TableShippingServicesWorkingCenterProps> = ({ setTableInstance, userPermissions, extraWhereParams, setOpened, tableInstance }) => {
    const { t } = useTwinTranslation()

    const columns: ColumnTableSchema<WorkingCenterShippingServiceModelTypeExtended> = [
        {
            id: 'order',
            dataKey: 'order',
            label: t('order', 'Orden'),
            sortable: true,
            width: 100,
        },
        {
            id: 'company',
            dataKey: 'ShippingService.ShippingCompany.name',
            label: t('company', 'Empresa'),
            minWidth: 130,
            sortable: true,
            customRender: (_parameterValue, allRowData) =><CustomRenderString value={allRowData['ShippingService.ShippingCompany.name']} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('company', 'Empresa') }
            },
        },
        {
            id: 'service',
            dataKey: 'ShippingService.name',
            label: t('service', 'Servicio'),
            minWidth: 130,
            sortable: true,
            customRender: (_parameterValue, allRowData) =><CustomRenderString value={allRowData['ShippingService.name']} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('service', 'Servicio') }
            },
        },
        {
            id: 'is_main',
            dataKey: 'is_main',
            label: t('byDefault', 'Por defecto'),
            customRender: (parameterValue, allRowData) => <CustomRenderBoolean className='cursor-pointer' onClick={() => setOpened({
                type: 'edit',
                allRowData
            })} value={String(parameterValue) === 'true'} width={56}/>,
        },
        {
            id: 'delete',
            dataKey: 'ShippingServiceId',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => <DeleteRow key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />,
            havePermission: permissionCheck(userPermissions, 'delete')
        }
    ]
    const children = useMemo(() => <ShippingServiceWorkingCenterSortButton tableInstance={tableInstance} userPermissions={userPermissions} extraWhereParams={extraWhereParams} />, [tableInstance, userPermissions, extraWhereParams])

    return (
        <TwinTable name='shippingServicesOfWorkingCenter' columns={columns} getDataFrom='/api/app/workingCenter/workingCenterShippingService/getAllWorkingCenterShippingServices'
            setTableInstance={setTableInstance} extraWhereParams={extraWhereParams} defaultOrder={{ defaultOrderField: 'order', order: 'asc' }}>
            {children}
        </TwinTable>             
    )
}

interface ModalCustomRenderBooleanProps extends ModalCustomRenderBooleanLogicProps {
}

const ModalCustomRenderBoolean: React.FC<ModalCustomRenderBooleanProps> = ({setOpened, ...logic}) => {
    const { t } = useTwinTranslation()
    const { updateShippingServiceMain } = useModalCustomRenderBooleanLogic({  setOpened, ...logic})
    return (
        <ModalSmallFilled translations={{
            title: t('sureUpdateCustomRenderBoolean', '¿Seguro que quieres modificarlo?'),
            subtitle: t('changeDefaultState', 'Cambiará el estado en por defecto'),
            buttonAccept: t('accept', 'Acceptar')
        }} onAccept={updateShippingServiceMain} opened={true} setOpened={() => setOpened(null)} />
    )
}


interface ModalCustomRenderBooleanLogicProps extends ModalEditComponentProps {
}

const useModalCustomRenderBooleanLogic = ({allRowData, onSubmit}: ModalCustomRenderBooleanLogicProps) => {
    const updateShippingServiceMain = useCallback(async () => {
        const result = await twinFetchPostText('/api/app/workingCenter/workingCenterShippingService/updateWorkingCenterShippingService', {
            ShippingServiceId: allRowData?.ShippingServiceId,
            WorkingCenterId: allRowData?.WorkingCenterId,
            is_main: !allRowData?.is_main
        })
        if (result) {
            onSubmit?.(result, {})
        }
    }, [allRowData, onSubmit])
    return { updateShippingServiceMain }
}


export default ShippingServicesWorkingCenter